import React, { useState, useMemo } from 'react';
import { TAROT_DECK, CARD_COUNT_IN_SPREAD, PICK_LIMIT } from '../../data/tarotDeck';
import { SITE_CONTENT } from '../../data/siteContent';
import * as Icons from 'lucide-react';
import { Moon, Sparkles, RefreshCcw } from 'lucide-react';

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function TarotCard({ card, revealed, onClick, index }) {
  const DynIcon = Icons[card.icon] || Moon;
  return (
    <button
      onClick={onClick}
      disabled={revealed}
      className={[
        'group relative w-[60px] h-[90px] sm:w-[90px] sm:h-[135px] md:w-[120px] md:h-[180px] rounded-[14px] sm:rounded-[18px]',
        'border border-white/60',
        'shadow-[0_12px_28px_-14px_rgba(120,80,140,0.55)]',
        'transition-[transform,box-shadow] duration-500 ease-out',
        'focus:outline-none focus:ring-2 focus:ring-[#b89dd4]/60',
        !revealed && 'hover:-translate-y-1 hover:rotate-[-1.5deg]'
      ].filter(Boolean).join(' ')}
      style={{ transformStyle: 'preserve-3d' }}
    >
      <div
        className={[
          'absolute inset-0 rounded-[18px] flex items-center justify-center',
          'transition-opacity duration-500',
          revealed ? 'opacity-0' : 'opacity-100',
          'bg-gradient-to-br from-[#d7c4eb] via-[#c9a7d9] to-[#b88bc8]'
        ].join(' ')}
      >
        <div className="flex flex-col items-center gap-1 sm:gap-2 text-white/90">
          <Moon className="w-4 h-4 sm:w-6 sm:h-6 md:w-8 md:h-8" strokeWidth={1.6} />
          <span className="text-[0.55rem] sm:text-[0.65rem] md:text-[0.7rem] tracking-[0.2em] uppercase">Elige</span>
        </div>
        <span className="absolute top-2 left-2 w-1.5 h-1.5 rounded-full bg-white/60" />
        <span className="absolute bottom-2 right-2 w-1.5 h-1.5 rounded-full bg-white/60" />
      </div>

      <div
        className={[
          'absolute inset-0 rounded-[18px] flex flex-col items-center justify-center px-2',
          'transition-opacity duration-500',
          revealed ? 'opacity-100' : 'opacity-0',
          'bg-gradient-to-b from-[#fbeed7] via-[#f7e2b4] to-[#f2d48a]'
        ].join(' ')}
      >
        <DynIcon className="w-5 h-5 sm:w-7 sm:h-7 md:w-9 md:h-9 text-[#6f4b8e]" strokeWidth={1.4} />
        <span className="mt-1 sm:mt-2 md:mt-3 font-serif-display text-[0.65rem] sm:text-[0.82rem] md:text-[0.95rem] text-[#4b3a2d] text-center leading-tight px-1">
          {card.name}
        </span>
      </div>
    </button>
  );
}

export default function Tarot() {
  const { title, description, resetLabel } = SITE_CONTENT.tarot;
  const [seed, setSeed] = useState(0);
  const [picked, setPicked] = useState([]); // indices

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const spread = useMemo(() => shuffle(TAROT_DECK).slice(0, CARD_COUNT_IN_SPREAD), [seed]);

  const handlePick = (idx) => {
    if (picked.includes(idx) || picked.length >= PICK_LIMIT) return;
    setPicked([...picked, idx]);
  };

  const reset = () => {
    setPicked([]);
    setSeed((s) => s + 1);
  };

  const revealedCards = picked.map((i) => spread[i]);

  return (
    <section className="w-full flex flex-col items-center">
      <div className="w-full max-w-5xl mx-auto">
        <h2 className="text-center font-serif-display text-[1.85rem] md:text-[2.1rem] text-[#3f2e22] flex items-center justify-center gap-3">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#ead6f5]">
            <Sparkles className="w-4 h-4 text-[#7a5aa2]" />
          </span>
          {title}
        </h2>
        <p className="mt-3 text-center text-[#6b5440] font-body max-w-xl mx-auto leading-relaxed">
          {description}
        </p>
        <p className="mt-2 text-center text-[#a27a57] font-body text-[0.85rem]">
          Cartas elegidas: <span className="font-semibold text-[#7a5aa2]">{picked.length}</span> / {PICK_LIMIT}
        </p>

        <div className="mt-10 grid grid-cols-5 gap-2 sm:gap-3 md:gap-5 justify-items-center">
          {spread.slice(0, 5).map((c, i) => (
            <TarotCard key={`r1-${i}-${seed}`} card={c} revealed={picked.includes(i)} onClick={() => handlePick(i)} index={i} />
          ))}
        </div>
        <div className="mt-3 sm:mt-5 grid grid-cols-5 gap-2 sm:gap-3 md:gap-5 justify-items-center">
          {spread.slice(5, 10).map((c, i) => {
            const realIdx = i + 5;
            return (
              <TarotCard key={`r2-${i}-${seed}`} card={c} revealed={picked.includes(realIdx)} onClick={() => handlePick(realIdx)} index={realIdx} />
            );
          })}
        </div>

        {revealedCards.length > 0 && (
          <div className="mt-10 rounded-[26px] bg-[#fbeed7]/80 border border-[#ead6b4]/70 px-6 md:px-10 py-7 shadow-[0_14px_40px_-20px_rgba(120,80,60,0.25)]">
            <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 font-serif-display text-[1.1rem] text-[#3f2e22]">
              {revealedCards.map((c, i) => {
                const DynIcon = Icons[c.icon] || Moon;
                return (
                  <span key={i} className="inline-flex items-center gap-2">
                    <DynIcon className="w-4 h-4 text-[#7a5aa2]" />
                    {c.name}
                    {i < revealedCards.length - 1 && <span className="text-[#c28fb4] ml-4">♦</span>}
                  </span>
                );
              })}
            </div>
            <p className="mt-5 text-center text-[#5b4632] font-body leading-[1.9]">
              {revealedCards.map((c) => c.meaning).join(' ')}
            </p>
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <button
            onClick={reset}
            className="px-6 py-3 rounded-full bg-gradient-to-r from-[#cdb2e0] to-[#b89dd4] text-white font-medium tracking-wide text-[0.92rem] inline-flex items-center gap-2 shadow-[0_10px_22px_-10px_rgba(155,120,190,0.6)] hover:-translate-y-0.5 transition-transform duration-300"
          >
            <RefreshCcw className="w-4 h-4" />
            {resetLabel}
          </button>
        </div>
      </div>
    </section>
  );
}
